import { Signal } from "@lit-labs/signals";
import type { CreatorItem } from "../creator-select/types";
import type {
  IWritableLiveEventFormState,
  ILiveEventFormState,
  LiveEventFormData,
  ValidationErrors,
  UIToggleState,
  AvailableLink,
  StreamSource,
} from "./types";

export class LiveEventFormState implements IWritableLiveEventFormState {
  readonly formData = new Signal.State<LiveEventFormData>({
    title: "",
    description: "",
    feeEuros: "",
    scheduledDate: "",
    scheduledTime: "",
    maxAttendees: "",
    whatsappUrl: "",
    ctaText: "",
    durationMinutes: "",
    language: "nl",
  });

  readonly validationErrors = new Signal.State<ValidationErrors>({});
  readonly isSubmitting = new Signal.State<boolean>(false);

  readonly uiToggles = new Signal.State<UIToggleState>({
    showDescription: false,
    showMaxAttendees: false,
    showWhatsApp: false,
    showCtaText: false,
    showLinkedLinks: false,
    showCoPromotion: false,
    showCommunityOnly: false,
  });

  readonly contentTier = new Signal.State<"free" | "premium">("premium");
  readonly thumbnailFileId = new Signal.State<string | undefined>(undefined);
  readonly thumbnailUrl = new Signal.State<string | undefined>(undefined);
  readonly linkedLinkIds = new Signal.State<string[]>([]);
  readonly availableLinks = new Signal.State<AvailableLink[]>([]);
  readonly isLoadingLinks = new Signal.State<boolean>(false);
  readonly eventId = new Signal.State<string | undefined>(undefined);
  readonly provisionalEventId = new Signal.State<string | undefined>(undefined);
  readonly isLoading = new Signal.State<boolean>(false);
  readonly streamSource = new Signal.State<StreamSource>("webcam");
  readonly isDraft = new Signal.State<boolean>(false);
  readonly collaboratorHandles = new Signal.State<string[]>([]);
  readonly existingCollaborators = new Signal.State<CreatorItem[]>([]);
  readonly communityOnly = new Signal.State<boolean>(false);
  readonly communityIds = new Signal.State<string[]>([]);
  readonly availableCommunities = new Signal.State<Array<{ value: string; label: string }>>([]);
  readonly isLoadingCommunities = new Signal.State<boolean>(false);

  reset(): void {
    this.formData.set({
      title: "",
      description: "",
      feeEuros: "",
      scheduledDate: "",
      scheduledTime: "",
      maxAttendees: "",
      whatsappUrl: "",
      ctaText: "",
      durationMinutes: "",
      language: "nl",
    });
    this.validationErrors.set({});
    this.isSubmitting.set(false);
    this.uiToggles.set({
      showDescription: false,
      showMaxAttendees: false,
      showWhatsApp: false,
      showCtaText: false,
      showLinkedLinks: false,
      showCoPromotion: false,
      showCommunityOnly: false,
    });
    this.contentTier.set("premium");
    this.thumbnailFileId.set(undefined);
    this.thumbnailUrl.set(undefined);
    this.linkedLinkIds.set([]);
    this.eventId.set(undefined);
    this.provisionalEventId.set(undefined);
    this.isLoading.set(false);
    this.streamSource.set("webcam");
    this.isDraft.set(false);
    this.collaboratorHandles.set([]);
    this.existingCollaborators.set([]);
    this.communityOnly.set(false);
    this.communityIds.set([]);
    this.availableCommunities.set([]);
    this.isLoadingCommunities.set(false);
  }
}

// Type cast for read-only access
export function asReadonlyLiveEventFormState(state: LiveEventFormState): ILiveEventFormState {
  return state as ILiveEventFormState;
}
